/**
 * @module ReasoningPathLadder
 * @purpose Five-rung semantic ladder rendering the full provenance chain for a cluster.
 * @depends types/reasoning, react-i18next
 * @dependedOnBy ReasoningClusterCard
 *
 * @rules
 * - Performance: pure display — no data fetching; data pre-resolved by parent
 * - State: none
 * - Errors: gracefully degrades when found:false rows are present
 */

import { useTranslation } from 'react-i18next'
import type { ClusterDetail, ClusterExplanationLine, ResolvedObservation } from '../../../../types/reasoning'
import { executionAssertionLabelKey, presenceAssertionLabelKey } from '../../../../lib/assertion-display'

const COMMENT_PREVIEW_LENGTH = 80
const RUNG_STYLE: React.CSSProperties = {
  paddingBottom: '12px',
  marginBottom: '12px',
  borderBottom: '1px solid var(--border)',
}
const RUNG_LAST_STYLE: React.CSSProperties = { ...RUNG_STYLE, borderBottom: 'none' }
const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted)',
  marginBottom: '4px',
}
const ITEM_STYLE: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--text)',
  padding: '2px 0',
}
const MUTED_ITALIC: React.CSSProperties = { color: 'var(--text-muted)', fontStyle: 'italic' }

interface ReasoningPathLadderProps {
  cluster: ClusterDetail
  resolved: Map<string, ResolvedObservation>
}

function deduplicateByIngestionRunId(observations: ResolvedObservation[]): ResolvedObservation[] {
  const seen = new Set<string>()
  const result: ResolvedObservation[] = []
  for (const obs of observations) {
    const runId = obs.ingestionRunId ?? ''
    if (!seen.has(runId)) {
      seen.add(runId)
      result.push(obs)
    }
  }
  return result
}

interface RungProps {
  label: string
  name: string
  children: React.ReactNode
  last?: boolean
}

function Rung({ label, name, children, last = false }: RungProps): React.ReactElement {
  return (
    <li className={`ladder-rung ladder-${name}`} style={last ? RUNG_LAST_STYLE : RUNG_STYLE}>
      <div style={LABEL_STYLE}>{label}</div>
      {children}
    </li>
  )
}

function ArtefactRungItems({
  orderedObs,
  noSourceLabel,
}: {
  orderedObs: ResolvedObservation[]
  noSourceLabel: string
}): React.ReactElement {
  return (
    <ol role="list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {orderedObs.map((obs) => (
        <li key={obs.observationId} style={ITEM_STYLE}>
          {obs.found
            ? (
                <>
                  {obs.sourceArtefactType ?? '—'}
                  {' · '}
                  {obs.sourceArtefactId ?? '—'}
                  {' · '}
                  {obs.hostName ?? obs.endpointId ?? '—'}
                </>
              )
            : <span style={MUTED_ITALIC}>{noSourceLabel}</span>}
        </li>
      ))}
    </ol>
  )
}

function IngestionRungItems({
  ingestionRows,
  qcRevokedLabel,
}: {
  ingestionRows: ResolvedObservation[]
  qcRevokedLabel: string
}): React.ReactElement {
  return (
    <ol role="list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {ingestionRows.map((obs) => (
        <li key={obs.ingestionRunId} style={ITEM_STYLE}>
          {obs.ingestionRunId ?? '—'}
          {' · '}
          {(obs.qcSignoffAt ?? null) === null
            ? <span style={MUTED_ITALIC}>{qcRevokedLabel}</span>
            : obs.qcSignoffAt}
        </li>
      ))}
    </ol>
  )
}

/**
 * Five-rung provenance ladder for a single cluster.
 *
 * @behaviour Renders Cluster → Comments → Observations → Source artefacts → Ingestion run.
 *            Gracefully handles found:false rows with placeholder copy.
 *            Export stub button surfaces a coming-soon alert on click.
 * @errors n/a — purely presentational
 */
export function ReasoningPathLadder({ cluster, resolved }: ReasoningPathLadderProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const orderedObs = Array.from(resolved.values())
  const ingestionRows = deduplicateByIngestionRunId(orderedObs.filter((obs) => obs.found))
  const noSourceLabel = t('reasoning.commentGroup.noSourceArtefact')
  const qcRevokedLabel = t('reasoning.reasoningPath.qcRevoked')

  function handleExportClick(): void {
    alert(t('reasoning.reasoningPath.export.comingSoon'))
  }

  return (
    <div style={{ fontSize: '0.8rem' }}>
      <ol className="reasoning-path-ladder" role="list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        <Rung name="cluster" label={t('reasoning.reasoningPath.rungs.cluster')}>
          <div style={ITEM_STYLE}>
            {cluster.clusterKind}
            {' · '}
            {cluster.canonicalDirectory}
            {' · #'}
            {cluster.rankPosition}
            {cluster.evidenceFlags.hasPresenceEvidence && ' · presence'}
            {cluster.evidenceFlags.hasReferenceEvidence && ' · reference'}
            {cluster.evidenceFlags.hasExecutionEvidence && ' · execution'}
          </div>
        </Rung>

        <Rung name="comments" label={t('reasoning.reasoningPath.rungs.comments')}>
          <ol role="list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {cluster.explanations.map((line: ClusterExplanationLine) => (
              <li key={line.commentType} style={ITEM_STYLE}>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                  {line.commentType}
                </span>
                {' — '}
                {line.text.slice(0, COMMENT_PREVIEW_LENGTH)}
                {line.text.length > COMMENT_PREVIEW_LENGTH && '…'}
              </li>
            ))}
          </ol>
        </Rung>

        <Rung name="observations" label={t('reasoning.reasoningPath.rungs.observations')}>
          <ol role="list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {orderedObs.map((obs) => (
              <li key={obs.observationId} style={ITEM_STYLE}>
                {obs.observationType ?? '—'}
                {' · '}
                {t(presenceAssertionLabelKey(obs.presenceAssertion))}
                {' · '}
                {t(executionAssertionLabelKey(obs.executionAssertion))}
              </li>
            ))}
          </ol>
        </Rung>

        <Rung name="artefacts" label={t('reasoning.reasoningPath.rungs.artefacts')}>
          <ArtefactRungItems orderedObs={orderedObs} noSourceLabel={noSourceLabel} />
        </Rung>

        <Rung name="ingestion-run" label={t('reasoning.reasoningPath.rungs.ingestionRun')} last>
          <IngestionRungItems ingestionRows={ingestionRows} qcRevokedLabel={qcRevokedLabel} />
        </Rung>
      </ol>

      <button
        type="button"
        onClick={handleExportClick}
        className="mt-2 text-xs uppercase tracking-wide underline hover:no-underline"
        style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}
      >
        {t('reasoning.reasoningPath.export.action')}
      </button>
    </div>
  )
}
